import { useCallback, type FC } from "react";

import { IconLock } from "@/assets/icons/geist/IconLock";
import { Avatar, AvatarInitials } from "@/components/Avatar";
import { useToast } from "@/components/flexkit/toast";
import { Heading4 } from "@/components/flexkit/typography/Headings";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/podkit/select/Select";
import { type ShareableResource } from "@/contexts/ShareResourceContext";
import { useResourceSharing } from "@/hooks/use-resource-sharing";
import { formatError } from "@/utils/errors";

type SharingAccessLevel = "shared" | "unshared";

type Props = {
    resource: ShareableResource;
    onAccessLevelChange?: (isSharedWithEveryone: boolean) => void;
};

export const GeneralAccessSection: FC<Props> = ({ resource, onAccessLevelChange }) => {
    const { toast } = useToast();
    const { isShared, share, unshare, isPending, orgName } = useResourceSharing(resource);

    const generalAccess: SharingAccessLevel = isShared ? "shared" : "unshared";

    const handleChange = useCallback(
        async (value: SharingAccessLevel) => {
            try {
                if (value === "shared" && !isShared) {
                    await share();
                } else if (value === "unshared" && isShared) {
                    await unshare();
                }

                toast({
                    title: "Sharing setting updated",
                });

                onAccessLevelChange?.(value === "shared");
            } catch (error) {
                toast({
                    title: "Failed to update sharing setting",
                    description: formatError(error),
                });
            }
        },
        [isShared, share, unshare, toast, onAccessLevelChange],
    );

    return (
        <div className="flex flex-col gap-2">
            <Heading4 className="font-medium">General access</Heading4>

            <Select value={generalAccess} onValueChange={handleChange}>
                <SelectTrigger loading={isPending} data-testid="general-access-select">
                    <div className="-ml-2">
                        <SelectValue />
                    </div>
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="shared" data-testid="option-everyone">
                        <div className="ml-2 flex items-center gap-2 text-base">
                            <Avatar size={16} className="size-5">
                                <AvatarInitials name={orgName} size={16} />
                            </Avatar>
                            <span>Everyone in {orgName}</span>
                        </div>
                    </SelectItem>
                    <SelectItem value="unshared" data-testid="option-groups-only">
                        <div className="ml-2 flex items-center gap-2 text-base">
                            <IconLock size="base" />
                            <span>Only groups and users with access</span>
                        </div>
                    </SelectItem>
                </SelectContent>
            </Select>
        </div>
    );
};
